/**
 * Global Article Generation Tracker for Dashboard
 *
 * Tracks article generation status across ALL announcements in real-time.
 * Used by dashboard to show generation badges on announcement cards.
 */

import { useState, useCallback, useEffect } from 'react';
import { useWebSocket } from './useWebSocket.js';

export interface GlobalGenerationState {
  announcementId: string;
  articlesGenerating: number;
  currentPhase: 'generating' | 'fact_checking' | 'polishing' | 'language_review';
  phaseLabel: string;
  style?: string;
}

export function useGlobalArticleGeneration() {
  const [generationsByAnnouncement, setGenerationsByAnnouncement] = useState<
    Map<string, GlobalGenerationState>
  >(new Map());

  // Connect to general announcements channel for all updates
  const wsChannels = ['assayerai:announcements'];

  const { connected } = useWebSocket({
    channels: wsChannels,
    onEvent: {
      status_change: (data) => {
        handleStatusChange(data);
      },
      article_created: (data) => {
        handleArticleCreated(data);
      },
    },
    autoInvalidate: false, // We manage state manually
    debug: false, // Keep quiet on dashboard
  });

  const handleStatusChange = useCallback((data: any) => {
    const { announcement_id, status, metadata } = data;

    // Clear tracking on completion or failure
    if (['completed', 'failed'].includes(status)) {
      setGenerationsByAnnouncement((prev) => {
        if (!prev.has(announcement_id)) return prev;
        const newMap = new Map(prev);
        newMap.delete(announcement_id);
        return newMap;
      });
      return;
    }

    // Only track generation-related statuses
    if (!['regenerating', 'generating', 'fact_checking', 'polishing', 'language_review'].includes(status)) {
      return;
    }

    // Map status to phase
    let phase: 'generating' | 'fact_checking' | 'polishing' | 'language_review' = 'generating';
    let phaseLabel = 'Generating article';

    if (status === 'fact_checking') {
      phase = 'fact_checking';
      phaseLabel = 'Fact-checking claims';
    } else if (status === 'polishing') {
      phase = 'polishing';
      phaseLabel = 'Polishing coherence';
    } else if (status === 'language_review') {
      phase = 'language_review';
      phaseLabel = 'Reviewing voice authenticity';
    }

    setGenerationsByAnnouncement((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(announcement_id);

      if (existing) {
        // Update phase
        newMap.set(announcement_id, {
          ...existing,
          currentPhase: phase,
          phaseLabel,
          style: metadata?.style || existing.style,
        });
      } else {
        // Create new entry
        newMap.set(announcement_id, {
          announcementId: announcement_id,
          articlesGenerating: 1,
          currentPhase: phase,
          phaseLabel,
          style: metadata?.style,
        });
      }

      return newMap;
    });
  }, []);

  const handleArticleCreated = useCallback((data: any) => {
    const { announcement_id } = data;

    // Remove from tracking after completion
    setTimeout(() => {
      setGenerationsByAnnouncement((prev) => {
        const newMap = new Map(prev);
        newMap.delete(announcement_id);
        return newMap;
      });
    }, 3000); // Keep for 3 seconds to show success
  }, []);

  // Get generation state for specific announcement
  const getGenerationState = useCallback(
    (announcementId: string): GlobalGenerationState | null => {
      return generationsByAnnouncement.get(announcementId) || null;
    },
    [generationsByAnnouncement]
  );

  // Get total count of announcements with active generation
  const getTotalGenerating = useCallback((): number => {
    return generationsByAnnouncement.size;
  }, [generationsByAnnouncement]);

  return {
    connected,
    generationsByAnnouncement,
    getGenerationState,
    getTotalGenerating,
  };
}
